import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  Button,
  IconButton,
  Chip,
  CircularProgress,
  Alert,
  TextField,
  InputAdornment,
  Paper,
  Avatar,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  ListItemSecondaryAction,
  Tooltip,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Switch,
  FormControlLabel,
} from "@mui/material";
import {
  Add as AddIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  Delete as DeleteIcon,
  OpenInNew as OpenInNewIcon,
  Close as CloseIcon,
  Link as LinkIcon,
  Key as KeyIcon,
} from "@mui/icons-material";
import axios from "axios";
import { hostname, isSafeUrl } from "../../utils";

interface ConnectedIntegration {
  id: string;
  toolkit: string;
  status: string;
  accountDisplay?: string;
  description?: string;
  logo?: string;
}

interface AvailableApp {
  name: string;
  slug: string;
  composioSlug: string;
  description: string;
  logo: string;
  categories: string[];
  authType: "oauth" | "api_key";
  supportsOAuth: boolean;
  supportsApiKey: boolean;
}

interface AuthField {
  name: string;
  displayName: string;
  type: string;
  description: string;
  required: boolean;
}

interface IntegrationsTabProps {
  getToken: () => Promise<string>;
  platformAccess: boolean;
  onPlatformAccessChange: (enabled: boolean) => void;
  composioToolsEnabled: boolean;
  onComposioToolsEnabledChange: (enabled: boolean) => void;
}

const IntegrationsTab: React.FC<IntegrationsTabProps> = ({
  getToken,
  platformAccess,
  onPlatformAccessChange,
  composioToolsEnabled,
  onComposioToolsEnabledChange,
}) => {
  const [connectedIntegrations, setConnectedIntegrations] = useState<ConnectedIntegration[]>([]);
  const [availableApps, setAvailableApps] = useState<AvailableApp[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingApps, setLoadingApps] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [connecting, setConnecting] = useState(false);
  const [disconnecting, setDisconnecting] = useState<string | null>(null);
  const [showBrowseDialog, setShowBrowseDialog] = useState(false);

  // Selected app for connection flow
  const [selectedApp, setSelectedApp] = useState<AvailableApp | null>(null);
  const [apiKey, setApiKey] = useState("");
  const [connectionError, setConnectionError] = useState("");
  const [authFields, setAuthFields] = useState<AuthField[]>([]);
  const [credentials, setCredentials] = useState<Record<string, string>>({});
  const [loadingAuthFields, setLoadingAuthFields] = useState(false);

  // Fetch connected integrations
  const fetchConnectedIntegrations = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const token = await getToken();
      const response = await axios.get(`${hostname}/personal-assistant/integrations`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setConnectedIntegrations(response.data.connected || []);
    } catch (err) {
      console.error("Error fetching integrations:", err);
      setError("Failed to load connected integrations");
    } finally {
      setLoading(false);
    }
  }, [getToken]);

  // Fetch available apps for browse dialog
  const fetchAvailableApps = useCallback(async (search?: string) => {
    try {
      setLoadingApps(true);
      const token = await getToken();
      const params = new URLSearchParams();
      if (search) params.append("search", search);

      const response = await axios.get(
        `${hostname}/personal-assistant/integrations/apps?${params.toString()}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setAvailableApps(response.data.apps || []);
    } catch (err) {
      console.error("Error fetching available apps:", err);
    } finally {
      setLoadingApps(false);
    }
  }, [getToken]);

  useEffect(() => {
    fetchConnectedIntegrations();
  }, [fetchConnectedIntegrations]);

  // Fetch apps when dialog opens or search changes (debounced)
  useEffect(() => {
    if (!showBrowseDialog) return;

    // Set loading immediately to prevent flash
    setLoadingApps(true);

    const timer = setTimeout(() => {
      fetchAvailableApps(searchQuery);
    }, searchQuery ? 300 : 0); // No delay on initial open

    return () => clearTimeout(timer);
  }, [searchQuery, showBrowseDialog, fetchAvailableApps]);

  // Handle selecting an app
  const handleSelectApp = async (app: AvailableApp) => {
    setSelectedApp(app);
    setApiKey("");
    setCredentials({});
    setConnectionError("");
    setAuthFields([]);

    // If it's an API key app, fetch the required auth fields
    if (app.authType === "api_key" && !app.supportsOAuth) {
      try {
        setLoadingAuthFields(true);
        const token = await getToken();
        const response = await axios.get(
          `${hostname}/personal-assistant/integrations/auth-fields/${app.composioSlug}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (response.data.fields && response.data.fields.length > 0) {
          setAuthFields(response.data.fields);
          // Initialize credentials object with empty values
          const initialCredentials: Record<string, string> = {};
          response.data.fields.forEach((field: AuthField) => {
            initialCredentials[field.name] = "";
          });
          setCredentials(initialCredentials);
        }
      } catch (err) {
        console.error("Error fetching auth fields:", err);
        // If we can't get fields, fall back to single API key field
        setAuthFields([]);
      } finally {
        setLoadingAuthFields(false);
      }
    }
  };

  // Handle back from app detail
  const handleBack = () => {
    setSelectedApp(null);
    setApiKey("");
    setCredentials({});
    setAuthFields([]);
    setConnectionError("");
  };

  // Handle OAuth connect
  const handleOAuthConnect = async () => {
    if (!selectedApp) return;

    try {
      setConnecting(true);
      setConnectionError("");
      const token = await getToken();

      const response = await axios.post(
        `${hostname}/personal-assistant/integrations/connect`,
        { appSlug: selectedApp.composioSlug },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.redirectUrl) {
        // Open OAuth in a popup window
        const width = 600;
        const height = 700;
        const left = window.screenX + (window.outerWidth - width) / 2;
        const top = window.screenY + (window.outerHeight - height) / 2;

        const safeUrlPattern = /^https?:\/\/[a-zA-Z0-9][a-zA-Z0-9.\-:/\\?&=%_+~#]+$/;
        if (!safeUrlPattern.test(response.data.redirectUrl)) return;
        const popup = window.open(
          response.data.redirectUrl,
          `${selectedApp.composioSlug}OAuth`,
          `width=${width},height=${height},left=${left},top=${top},toolbar=no,location=no,status=no,menubar=no,scrollbars=yes,resizable=yes`
        );

        // Poll for popup close
        const pollInterval = setInterval(async () => {
          if (popup?.closed) {
            clearInterval(pollInterval);
            setConnecting(false);
            // Refresh the list to see if connection was successful
            await fetchConnectedIntegrations();
            handleCloseDialog();
          }
        }, 1000);

        // Check if popup was blocked
        if (!popup || popup.closed || typeof popup.closed === "undefined") {
          clearInterval(pollInterval);
          setConnectionError("Popup was blocked. Please allow popups for this site and try again.");
          setConnecting(false);
        }
      }
    } catch (err: any) {
      console.error("Error connecting app:", err);
      setConnectionError(err.response?.data?.error || "Failed to connect integration");
      setConnecting(false);
    }
  };

  // Handle API key connect
  const handleApiKeyConnect = async () => {
    if (!selectedApp) return;

    // Check if we have dynamic fields or just a single API key
    const hasMultipleFields = authFields.length > 0;

    if (hasMultipleFields) {
      // Validate all required fields are filled
      const missingFields = authFields
        .filter((field) => field.required && !credentials[field.name]?.trim())
        .map((field) => field.displayName);

      if (missingFields.length > 0) {
        setConnectionError(`Please fill in: ${missingFields.join(", ")}`);
        return;
      }
    } else if (!apiKey.trim()) {
      setConnectionError("Please enter your API key");
      return;
    }

    try {
      setConnecting(true);
      setConnectionError("");
      const token = await getToken();

      const requestBody: any = {
        appSlug: selectedApp.composioSlug,
      };

      if (hasMultipleFields) {
        // Send credentials object with field names as keys
        requestBody.credentials = credentials;
      } else {
        // Legacy single API key flow
        requestBody.apiKey = apiKey.trim();
      }

      const response = await axios.post(
        `${hostname}/personal-assistant/integrations/connect-apikey`,
        requestBody,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        await fetchConnectedIntegrations();
        handleCloseDialog();
      } else {
        setConnectionError(response.data.error || "Failed to connect");
      }
    } catch (err: any) {
      console.error("Error connecting with API key:", err);
      setConnectionError(err.response?.data?.error || "Failed to connect with API key");
    } finally {
      setConnecting(false);
    }
  };

  // Handle disconnect
  const handleDisconnect = async (integration: ConnectedIntegration) => {
    try {
      setDisconnecting(integration.id);
      setError(null);
      const token = await getToken();

      await axios.post(
        `${hostname}/personal-assistant/integrations/disconnect`,
        { connectedAccountId: integration.id },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Remove from local state
      setConnectedIntegrations((prev) => prev.filter((i) => i.id !== integration.id));
    } catch (err: any) {
      console.error("Error disconnecting integration:", err);
      setError(err.response?.data?.error || "Failed to disconnect integration");
    } finally {
      setDisconnecting(null);
    }
  };

  // Close dialog and reset state
  const handleCloseDialog = () => {
    setShowBrowseDialog(false);
    setSelectedApp(null);
    setApiKey("");
    setCredentials({});
    setAuthFields([]);
    setConnectionError("");
    setSearchQuery("");
  };

  // Format toolkit name for display
  const formatToolkitName = (toolkit: string): string => {
    return toolkit
      .toLowerCase()
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  // Get Composio logo URL for a toolkit
  const getToolkitLogoUrl = (toolkit: string): string => {
    const slug = toolkit.toLowerCase().replace(/_/g, "");
    return `https://logos.composio.dev/api/${slug}`;
  };

  // Render app grid (browse view)
  const renderAppGrid = () => {
    if (loadingApps) {
      return (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress size={32} />
        </Box>
      );
    }

    if (availableApps.length === 0) {
      return (
        <Box sx={{ textAlign: "center", py: 4 }}>
          <Typography color="text.secondary">
            {searchQuery ? "No integrations found matching your search" : "No integrations available"}
          </Typography>
        </Box>
      );
    }

    return (
      <Grid container spacing={2}>
        {availableApps.map((app) => (
          <Grid item xs={12} sm={6} md={4} key={app.slug}>
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                height: 160,
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
                cursor: "pointer",
                transition: "all 0.2s",
                "&:hover": {
                  borderColor: "primary.main",
                  boxShadow: 1,
                },
              }}
              onClick={() => handleSelectApp(app)}
            >
              <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
                <Avatar
                  src={app.logo}
                  alt={app.name}
                  sx={{
                    width: 48,
                    height: 48,
                    flexShrink: 0,
                    bgcolor: "action.disabledBackground",
                  }}
                >
                  {app.name[0]}
                </Avatar>
                <Box sx={{ flex: 1, overflow: "hidden" }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <Typography variant="subtitle1" noWrap>
                      {app.name}
                    </Typography>
                    {app.authType === "api_key" && (
                      <KeyIcon sx={{ fontSize: 14, color: "text.secondary" }} />
                    )}
                  </Box>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{
                      display: "-webkit-box",
                      WebkitLineClamp: 3,
                      WebkitBoxOrient: "vertical",
                      overflow: "hidden",
                    }}
                  >
                    {app.description}
                  </Typography>
                </Box>
              </Box>
            </Paper>
          </Grid>
        ))}
      </Grid>
    );
  };

  // Render app install view (detail view for selected app)
  const renderAppInstall = () => {
    if (!selectedApp) return null;

    const isApiKeyOnly = selectedApp.authType === "api_key" && !selectedApp.supportsOAuth;
    const hasMultipleFields = authFields.length > 0;

    // Check if all required fields are filled
    const isFormValid = hasMultipleFields
      ? authFields.every((field) => !field.required || credentials[field.name]?.trim())
      : apiKey.trim();

    return (
      <Box sx={{ py: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
          <Avatar
            src={selectedApp.logo}
            alt={selectedApp.name}
            sx={{ width: 64, height: 64 }}
          >
            {selectedApp.name[0]}
          </Avatar>
          <Box>
            <Typography variant="h6">{selectedApp.name}</Typography>
            {selectedApp.description && (
              <Typography variant="body2" color="text.secondary">
                {selectedApp.description}
              </Typography>
            )}
          </Box>
        </Box>

        {connectionError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {connectionError}
          </Alert>
        )}

        {isApiKeyOnly ? (
          // API Key flow
          <>
            <Alert severity="info" sx={{ mb: 2 }}>
              This integration requires credentials. You can get these from {selectedApp.name}'s dashboard or settings.
            </Alert>

            {loadingAuthFields ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
                <CircularProgress size={24} />
              </Box>
            ) : hasMultipleFields ? (
              // Dynamic fields based on auth requirements
              <>
                {authFields.map((field) => (
                  <TextField
                    key={field.name}
                    fullWidth
                    label={`${field.displayName}${field.required ? " *" : ""}`}
                    type={field.name.toLowerCase().includes("key") || field.name.toLowerCase().includes("secret") || field.name.toLowerCase().includes("password") ? "password" : "text"}
                    value={credentials[field.name] || ""}
                    onChange={(e) =>
                      setCredentials((prev) => ({
                        ...prev,
                        [field.name]: e.target.value,
                      }))
                    }
                    sx={{ mb: 2 }}
                    helperText={field.description}
                  />
                ))}
              </>
            ) : (
              // Fallback to single API key field
              <TextField
                fullWidth
                label="API Key"
                type="password"
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                sx={{ mb: 3 }}
                helperText={`Enter your ${selectedApp.name} API key`}
              />
            )}

            <Button
              variant="contained"
              onClick={handleApiKeyConnect}
              disabled={connecting || !isFormValid || loadingAuthFields}
              startIcon={connecting && <CircularProgress size={16} color="inherit" />}
              fullWidth
              size="large"
              sx={{ mt: 1 }}
            >
              {connecting ? "Connecting..." : "Connect"}
            </Button>
          </>
        ) : (
          // OAuth flow
          <>
            <Alert severity="info" sx={{ mb: 2 }}>
              Connect your {selectedApp.name} account. A popup will open for authorization.
            </Alert>
            <Button
              variant="contained"
              onClick={handleOAuthConnect}
              disabled={connecting}
              startIcon={connecting ? <CircularProgress size={16} color="inherit" /> : <OpenInNewIcon />}
              fullWidth
              size="large"
            >
              {connecting ? "Connecting..." : `Connect ${selectedApp.name}`}
            </Button>
          </>
        )}
      </Box>
    );
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", py: 4 }}>
        <CircularProgress size={32} />
      </Box>
    );
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Typography variant="caption" color="text.secondary">
        Connect third-party services to give your assistant access to more tools and data.
      </Typography>

      {error && (
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Tool Access Settings */}
      <Box>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Tool Access
        </Typography>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          <FormControlLabel
            control={
              <Switch
                checked={platformAccess}
                onChange={(e) => onPlatformAccessChange(e.target.checked)}
              />
            }
            label={
              <Box>
                <Typography variant="body2">Enable platform API tools</Typography>
                <Typography variant="caption" color="text.secondary">
                  Allow the assistant to manage lists, sequences, agents, and other platform resources via API
                </Typography>
              </Box>
            }
          />
          <FormControlLabel
            control={
              <Switch
                checked={composioToolsEnabled}
                onChange={(e) => onComposioToolsEnabledChange(e.target.checked)}
              />
            }
            label={
              <Box>
                <Typography variant="body2">Enable Composio integration tools</Typography>
                <Typography variant="caption" color="text.secondary">
                  Allow the assistant to use your connected third-party integrations (Gmail, Slack, etc.)
                </Typography>
              </Box>
            }
          />
        </Box>
      </Box>

      {/* Connected Integrations */}
      <Box>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
          <Typography variant="subtitle2">
            Connected ({connectedIntegrations.length})
          </Typography>
          <Box sx={{ display: "flex", gap: 1 }}>
            <IconButton size="small" onClick={fetchConnectedIntegrations} title="Refresh">
              <RefreshIcon fontSize="small" />
            </IconButton>
            <Button
              size="small"
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={() => setShowBrowseDialog(true)}
            >
              Add Integration
            </Button>
          </Box>
        </Box>

        {connectedIntegrations.length === 0 ? (
          <Paper
            sx={{
              p: 3,
              textAlign: "center",
              backgroundColor: "action.hover",
              border: "1px dashed",
              borderColor: "divider",
            }}
          >
            <LinkIcon sx={{ fontSize: 40, color: "text.secondary", mb: 1 }} />
            <Typography variant="body2" color="text.secondary">
              No integrations connected yet
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 2 }}>
              Connect apps like GitHub, Slack, Google Calendar, and more
            </Typography>
            <Button
              variant="contained"
              size="small"
              startIcon={<AddIcon />}
              onClick={() => setShowBrowseDialog(true)}
            >
              Browse Integrations
            </Button>
          </Paper>
        ) : (
          <List sx={{ bgcolor: "background.paper", borderRadius: 1, border: "1px solid", borderColor: "divider" }}>
            {connectedIntegrations.map((integration, index) => (
              <React.Fragment key={integration.id}>
                {index > 0 && <Divider />}
                <ListItem>
                  <ListItemAvatar>
                    <Avatar
                      src={integration.logo || getToolkitLogoUrl(integration.toolkit)}
                      alt={formatToolkitName(integration.toolkit)}
                      sx={{ width: 40, height: 40 }}
                    >
                      {formatToolkitName(integration.toolkit)[0]}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        {formatToolkitName(integration.toolkit)}
                        <Chip
                          label={integration.status}
                          size="small"
                          color={integration.status === "ACTIVE" ? "primary" : "default"}
                        />
                      </Box>
                    }
                    secondary={
                      <Box>
                        {integration.accountDisplay && (
                          <Typography variant="body2" color="text.primary" sx={{ fontWeight: 500 }}>
                            {integration.accountDisplay}
                          </Typography>
                        )}
                        {integration.description && (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{
                              display: "-webkit-box",
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: "vertical",
                              overflow: "hidden",
                            }}
                          >
                            {integration.description}
                          </Typography>
                        )}
                      </Box>
                    }
                  />
                  <ListItemSecondaryAction>
                    <Tooltip title="Disconnect">
                      <IconButton
                        edge="end"
                        onClick={() => handleDisconnect(integration)}
                        disabled={disconnecting === integration.id}
                        color="error"
                      >
                        {disconnecting === integration.id ? (
                          <CircularProgress size={20} />
                        ) : (
                          <DeleteIcon />
                        )}
                      </IconButton>
                    </Tooltip>
                  </ListItemSecondaryAction>
                </ListItem>
              </React.Fragment>
            ))}
          </List>
        )}
      </Box>

      {/* Browse Dialog */}
      <Dialog
        open={showBrowseDialog}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { height: "85vh", maxHeight: 800 } }}
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          {selectedApp ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Button variant="text" onClick={handleBack} sx={{ minWidth: "auto" }}>
                Back
              </Button>
              <Typography>/ Connect {selectedApp.name}</Typography>
            </Box>
          ) : (
            "Browse Integrations"
          )}
          <IconButton onClick={handleCloseDialog} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ display: "flex", flexDirection: "column", p: 0 }}>
          {!selectedApp && (
            <>
              {/* Search */}
              <Box sx={{ px: 3, py: 2 }}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Search integrations..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon color="action" />
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>

              {/* Apps Grid */}
              <Box sx={{ flex: 1, overflow: "auto", px: 3, pb: 2 }}>
                {renderAppGrid()}
              </Box>
            </>
          )}

          {selectedApp && (
            <Box sx={{ px: 3 }}>
              {renderAppInstall()}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={handleCloseDialog} disabled={connecting}>
            {selectedApp ? "Cancel" : "Close"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default IntegrationsTab;
